import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Scale,
  HandCoins,
  Sparkles,
  Briefcase,
  TrendingUp,
  FileSearch,
  Users,
  ArrowRight,
} from "lucide-react";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Responsibility of Consulting",
    body:
      "Our approach is based on long-term prospects, and each transaction is an opportunity to confirm the reputation of a competent consulting company.",
  },
  {
    icon: FileSearch,
    title: "Quality of Reports",
    body:
      "Information from our reports cannot be found in any open sources. Market research is individual, reliable and produced exclusively for you.",
  },
  {
    icon: Scale,
    title: "Integrity in Consulting",
    body:
      "Transparency and honesty are essential to long-term relationships. Each transaction is approached with a firm commitment to ethical standards.",
  },
  {
    icon: Sparkles,
    title: "Tailored Solutions",
    body:
      "Rooted in a thorough understanding of your unique challenges and goals, we develop customised strategies that are both innovative and effective.",
  },
];

const OFFERINGS = [
  {
    icon: HandCoins,
    title: "Wealth Management",
    body:
      "Comprehensive wealth preservation and growth strategies tailored to your unique financial goals.",
    to: "/services",
  },
  {
    icon: TrendingUp,
    title: "Trading & Investments",
    body: "Strategic investments in watches, gold, crypto assets, and financial markets.",
    to: "/borrowers-investors",
  },
  {
    icon: FileSearch,
    title: "Due Diligence",
    body: "Rigorous KYC compliance and thorough investigation for all investments.",
    to: "/due-diligence",
  },
  {
    icon: Users,
    title: "Family Office",
    body: "Exclusive services for high-net-worth families seeking multi-generational wealth transfer.",
    to: "/services",
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page" className="page-fade">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden bg-[#070B14]">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/14523757/pexels-photo-14523757.jpeg"
            alt="Modern London skyscrapers at night"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070B14]/40 via-[#070B14]/70 to-[#070B14]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070B14] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-32 pb-24 w-full">
          <div className="rise text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
            EMIVIA LIMITED
          </div>
          <h1
            data-testid="home-hero-title"
            className="rise rise-delay-1 mt-8 font-serif-display text-white text-5xl sm:text-7xl lg:text-[88px] leading-[0.98] tracking-tight max-w-5xl"
          >
            Premier UK <br />
            <span className="italic font-normal text-[#D4AF37]">Financial</span> Expert.
          </h1>
          <p className="rise rise-delay-2 mt-8 max-w-2xl text-white/75 text-base sm:text-lg font-light leading-relaxed">
            Expertise in luxury asset investments, gold, and digital currencies.
            Built on UK precision, transparency, and trust.
          </p>

          <div className="rise rise-delay-3 mt-12 flex flex-wrap items-center gap-5">
            <Link
              to="/services"
              data-testid="home-cta-services"
              className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] bg-[#D4AF37] text-[#070B14] px-8 py-4 hover:bg-[#F3CE52] transition-colors duration-300"
            >
              Our Services <ArrowRight size={14} />
            </Link>
            <Link
              to="/contact"
              data-testid="home-cta-contact"
              className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] border border-white/20 text-white px-8 py-4 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
            >
              Speak with us
            </Link>
          </div>

          {/* Stats */}
          <div className="rise rise-delay-3 mt-24 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-3xl">
            {[
              { k: "20+", l: "Years team experience" },
              { k: "100%", l: "KYC compliance" },
              { k: "UK", l: "Independent expert" },
            ].map((s) => (
              <div key={s.l} className="bg-[#070B14]/90 backdrop-blur px-8 py-8">
                <div className="font-serif-display text-[#D4AF37] text-4xl sm:text-5xl">{s.k}</div>
                <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-white/60">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="relative py-24 sm:py-32 bg-[#070B14]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
              About Us
            </div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Built for <br />
              <span className="italic">Long-Term Success.</span>
            </h2>
            <div className="mt-10 aspect-[4/5] relative overflow-hidden">
              <img
                src="https://images.pexels.com/photos/8442325/pexels-photo-8442325.jpeg"
                alt="Gold bars representing wealth preservation"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-7 lg:pt-24">
            <div className="space-y-7 text-white/75 text-base sm:text-lg leading-relaxed font-light max-w-2xl">
              <p>
                EMIVIA LIMITED was built with a long-term perspective of the
                direct lending market. We believe that to be successful, we
                must go beyond originating attractive investments — we must
                also provide investors with the transparency, service and
                flexibility of an in-house investment team.
              </p>
              <p>
                EMIVIA LIMITED is a private investment company specialising as
                an independent adviser in Asset & Wealth management. We are
                based in the United Kingdom since 2021.
              </p>
              <p>
                Our responsiveness and independence in the continuous pursuit
                of excellence have kept us at the forefront of the UK&apos;s
                Asset & Wealth management industry. Our success is based
                primarily on the quality of the objective and impartial advice
                we offer to high-net-worth individuals, families, entrepreneurs
                and institutions.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-8 max-w-md">
              <div>
                <div className="font-serif-display text-[#D4AF37] text-4xl">2021</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.28em] text-white/50">
                  Founded in the UK
                </div>
              </div>
              <div>
                <div className="font-serif-display text-[#D4AF37] text-4xl">UK</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.28em] text-white/50">
                  Regulated framework
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative py-24 sm:py-32 bg-[#0B1120] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
              Our Values
            </div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              What Sets Us Apart.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal
                  key={v.title}
                  delay={i * 80}
                  className="group bg-[#070B14] p-10 sm:p-12 transition-colors duration-500 hover:bg-[#0F1523]"
                >
                  <Icon size={28} strokeWidth={1.25} className="text-[#D4AF37]" />
                  <h3 className="mt-7 font-serif-display text-white text-2xl sm:text-3xl tracking-tight">
                    {v.title}
                  </h3>
                  <p className="mt-5 text-white/65 text-sm sm:text-base leading-relaxed font-light max-w-md">
                    {v.body}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="relative py-24 sm:py-32 bg-[#070B14]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8">
            <div className="max-w-xl">
              <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
                What We Offer
              </div>
              <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
                Comprehensive <br />
                <span className="italic">Financial Services.</span>
              </h2>
            </div>
            <Link
              to="/services"
              className="link-gold text-[11px] uppercase tracking-[0.32em] hidden sm:inline-block"
              data-testid="home-all-services"
            >
              View all services →
            </Link>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {OFFERINGS.map((o, i) => {
              const Icon = o.icon;
              return (
                <Reveal
                  key={o.title}
                  delay={i * 90}
                  className="group flex flex-col bg-[#070B14] p-8 sm:p-10 transition-all duration-500 hover:bg-[#0F1523]"
                >
                  <Icon size={26} strokeWidth={1.25} className="text-[#D4AF37]" />
                  <h3 className="mt-8 font-serif-display text-white text-2xl tracking-tight">
                    {o.title}
                  </h3>
                  <p className="mt-4 text-white/60 text-sm leading-relaxed font-light flex-1">
                    {o.body}
                  </p>
                  <Link
                    to={o.to}
                    data-testid={`home-offer-${o.title.toLowerCase().replace(/\s|&/g, "-")}`}
                    className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] group-hover:gap-3 transition-all"
                  >
                    Learn more <ArrowRight size={13} />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CtaSection testId="home-cta" />
    </div>
  );
}
